import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://staketek.com.au";

/**
 * Per-page SEO component.
 * Pass title, description, path (e.g. "/products/brackets"), image (absolute or "/brand/...").
 * Optionally pass JSON-LD structured data via `jsonLd` (object or array of objects).
 */
export default function Seo({ title, description, path = "/", image, keywords, jsonLd, noIndex = false, type = "website" }) {
  const url = `${SITE}${path}`;
  const fullTitle = title.includes("StakeTek") ? title : `${title} · StakeTek`;
  const ogImage = image ? (image.startsWith("http") ? image : `${SITE}${image}`) : `${SITE}/brand/website-hero.jpg`;

  useEffect(() => {
    document.querySelectorAll("[data-seo-fallback='true']").forEach((tag) => tag.remove());
  }, []);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"}
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="StakeTek" />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}
    </Helmet>
  );
}

// The JSON-LD builders live in lib/schema.js so the build-time prerender script
// can use the exact same code (it can't import this file: JSX + React). They're
// re-exported here so every page's existing `from "../components/Seo"` import
// keeps working untouched.
export {
  breadcrumbJsonLd,
  faqPageJsonLd,
  articleJsonLd,
  howToJsonLd,
  productJsonLd,
  productGroupJsonLd,
} from "../lib/schema";
